/* Geofenced check-in / check-out for the signed-in employee (POST /attendance/check). */
import { useState } from "react";
import { ApiError } from "../../data/provider";
import { useAttendanceMonth, useCheck } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { Icon, cx, errMsg, useToast } from "../../ui";
import { periodOf } from "./essLib";

/** Office geofence used for the demo check-in (Al Waha HQ, Jeddah). */
export const OFFICE = { lat: 21.5433, lng: 39.1728 };

export function useCheckState(employeeId: number, today: string) {
  const month = useAttendanceMonth(employeeId, periodOf(today));
  const row = month.data?.find((r) => r.date === today) ?? null;
  const [local, setLocal] = useState<{ kind: "in" | "out"; at: string } | null>(null);
  const derivedIn = !!row?.firstIn && !row?.lastOut;
  const checkedIn = local ? local.kind === "in" : derivedIn;
  const inAt = local?.kind === "in" ? local.at : row?.firstIn ?? null;
  return { row, checkedIn, inAt, setLocal, loading: month.isLoading };
}

export function CheckButton({ employeeId, today, variant = "hero" }: { employeeId: number; today: string; variant?: "hero" | "buttons" }) {
  const { t, L } = useI18n();
  const toast = useToast();
  const check = useCheck();
  const st = useCheckState(employeeId, today);
  const run = async (kind: "in" | "out") => {
    try {
      const p = await check.mutateAsync({ employeeId, kind, lat: OFFICE.lat, lng: OFFICE.lng });
      const hm = p.time.slice(11, 16);
      st.setLocal({ kind, at: hm });
      toast(kind === "in" ? L("Checked in at " + hm + " · location verified", "تم تسجيل الحضور " + hm + " · تم التحقق من الموقع") : L("Checked out at " + hm + " · have a good evening", "تم تسجيل الانصراف " + hm + " · مساء سعيد"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        st.setLocal({ kind, at: st.inAt ?? "" });
        toast(kind === "in" ? L("You're already checked in.", "أنت مسجل الحضور بالفعل.") : L("You're already checked out.", "سجلت الانصراف بالفعل."), "info");
      } else toast(errMsg(e), "bad");
    }
  };
  if (variant === "buttons") {
    return (
      <div className="row" data-testid="check-buttons">
        <button type="button" className="btn pri" disabled={check.isPending || st.checkedIn} onClick={() => run("in")} data-testid="btn-check-in"><Icon name="map-pin" /><span>{t("Check in")}</span></button>
        <button type="button" className="btn" disabled={check.isPending || !st.checkedIn} onClick={() => run("out")} data-testid="btn-check-out"><Icon name="log-out" /><span>{t("Check out")}</span></button>
      </div>
    );
  }
  return (
    <>
      <div className="geo"><span className="pulse" />{t("You're inside the office geofence")}</div>
      <button type="button" className={cx("checkin", st.checkedIn && "out")} disabled={check.isPending || st.loading} onClick={() => run(st.checkedIn ? "out" : "in")} data-testid="btn-checkin">
        <Icon name={st.checkedIn ? "log-out" : "map-pin"} className="l" />{st.checkedIn ? t("Check out") : t("Check in")}
      </button>
      {st.checkedIn
        ? <div className="sm muted" data-testid="checkin-status">{t("Checked in")} · {st.inAt} · {L("Al Waha HQ, Jeddah", "مقر الواحة، جدة")}</div>
        : <div className="sm muted" data-testid="checkin-status">{L("Shift 08:00–17:00 · Al Waha HQ, Jeddah", "الوردية 08:00–17:00 · مقر الواحة، جدة")}{st.row?.lastOut ? " · " + L("Checked out ", "انصراف ") + st.row.lastOut : ""}</div>}
    </>
  );
}
